import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';

import type { TeamsChatSession } from '../../hooks/useTeamsChatSession';
import { TeamsChatComposer } from './TeamsChatComposer';
import { TeamsChatList } from './TeamsChatList';
import { TeamsChatThread } from './TeamsChatThread';

interface TeamsChatBodyProps {
  session: TeamsChatSession;
}

/**
 * Everything below the chrome: the connection states, the list/thread switch and
 * the composer.
 *
 * Shared verbatim by the drawer and the standalone `/teams-chat` page, which is
 * the point — the two differ only in what frames them. It renders nothing of its
 * own layout beyond a column, so the caller decides the width and the height.
 */
export function TeamsChatBody({ session }: TeamsChatBodyProps) {
  const { t } = useTranslation();

  if (!session.configured) {
    // Unreachable from the drawer, which renders nothing at all when Teams is
    // unconfigured. The page can be reached by URL, so it needs an answer.
    return (
      <StateMessage
        severity="info"
        title={t('teams.panel.notConfiguredTitle')}
        detail={t('teams.panel.notConfiguredBody')}
      />
    );
  }

  if (!session.connected) {
    return (
      <StateMessage
        severity="info"
        title={t('teams.panel.notConnectedTitle')}
        detail={t('teams.panel.notConnectedBody')}
        action={
          <Button component={RouterLink} to="/profile" size="small" variant="outlined">
            {t('teams.panel.notConnectedCta')}
          </Button>
        }
      />
    );
  }

  if (session.needsReconnect) {
    return (
      <StateMessage
        severity="warning"
        title={t('teams.panel.reconnectTitle')}
        detail={t('teams.panel.reconnectBody')}
        action={
          <Button
            component={RouterLink}
            to="/profile"
            size="small"
            variant="contained"
            color="warning"
          >
            {t('teams.panel.notConnectedCta')}
          </Button>
        }
      />
    );
  }

  const { activeChat } = session;

  if (session.activeChatId && activeChat) {
    return (
      <>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <TeamsChatThread
            chat={activeChat}
            messages={session.messages}
            isLoading={session.isMessagesLoading}
            errorMessage={session.messagesErrorMessage}
            presence={
              activeChat.presenceUserId
                ? session.presence?.get(activeChat.presenceUserId)
                : undefined
            }
            avatarsAvailable={session.avatarsAvailable}
            filesAvailable={session.filesAvailable}
            hasMore={session.hasMoreMessages}
            isLoadingMore={session.isLoadingMoreMessages}
            onLoadMore={session.loadMoreMessages}
            onBack={session.clearActiveChat}
            onRetry={session.retryMessages}
          />
        </Box>
        {/* Reached only when a chat is open and the connection is healthy —
            both are preconditions of this branch. */}
        <TeamsChatComposer
          chatId={activeChat.id}
          // Only where "everyone" means more than one person: in a one-to-one
          // it is a second way to ping the person already at the top of the
          // list, and for an `unknown` chat type we cannot say what it is.
          groupMentionLabel={
            activeChat.chatType === 'group' || activeChat.chatType === 'meeting'
              ? activeChat.title
              : undefined
          }
          onSend={session.send}
          isSending={session.isSending}
          errorMessage={session.didSendFail ? t('teams.panel.sendFailed') : undefined}
          onDismissError={session.dismissSendError}
        />
      </>
    );
  }

  return (
    <TeamsChatList
      chats={session.chats}
      activeChatId={session.activeChatId ?? undefined}
      isLoading={session.isChatsLoading}
      errorMessage={session.chatsErrorMessage}
      presence={session.presence}
      avatarsAvailable={session.avatarsAvailable}
      onSelect={session.selectChat}
      onSetPrefs={session.setChatPrefs}
      onMarkRead={session.markRead}
      onMarkAllRead={session.markAllRead}
      onSetMuted={session.setChatsMuted}
      onRetry={session.retryChats}
    />
  );
}

interface StateMessageProps {
  severity: 'info' | 'warning';
  title: string;
  detail: string;
  action?: React.ReactNode;
}

/** Full-panel notice for the not-connected / needs-reconnect states. */
function StateMessage({ severity, title, detail, action }: StateMessageProps) {
  return (
    <Box sx={{ p: 2 }}>
      <Alert severity={severity} sx={{ '& .MuiAlert-message': { minWidth: 0 } }}>
        <AlertTitle sx={{ fontSize: '0.875rem' }}>{title}</AlertTitle>
        <Typography variant="body2" sx={{ mb: action ? 1.5 : 0 }}>
          {detail}
        </Typography>
        {action}
      </Alert>
    </Box>
  );
}
